import {Component} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {firstValueFrom} from 'rxjs';
import {IBaseCRUModalComponent} from '../../../../sdk/src/components/ibase-cru-modal/ibase-cru-modal.component';
import {PrimeNgImportsModule} from '../../../../sdk/src/prime-ng-imports';
import {EDialogOpenMode} from '../../../../sdk/src/enums/dialog-open-mode.enum';
import {CpmCS3BucketApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-global/cpmc-s3-bucket.api-service';
import {CpmCS3BucketApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-global/cpmc-s3-bucket.api-model';
import {
    CpmCS3BucketCreateApiSchema,
    CpmCS3BucketUpdateApiSchema
} from '../../../../gen/cpm-c-api-ts/src/schemas/c-global/cpmc-s3-bucket.api-schemas';
import {SdkImportsModule} from '../../../../sdk/src/sdk-imports';

export class NewEditS3BucketModalConfig {
    constructor(public existing?: CpmCS3BucketApiModel) {}
}
export class NewEditS3BucketModalResult {
    constructor(public template?: CpmCS3BucketApiModel) {}
}

/**
 * New/Edit S3Bucket modal
 */
@Component({
    selector: 'app-new-edit-s3-bucket-modal-modal',
    templateUrl: './new-edit-s3-bucket-modal.component.html',
    imports: [ReactiveFormsModule, PrimeNgImportsModule, SdkImportsModule]
})
export class NewEditS3BucketModalComponent extends IBaseCRUModalComponent<
    CpmCS3BucketApiModel,
    NewEditS3BucketModalComponent,
    NewEditS3BucketModalConfig,
    NewEditS3BucketModalResult
> {
    protected item!: CpmCS3BucketApiModel;
    protected _booleanOptions = [
        {
            label: 'Yes: Ensure certificates validity',
            value: true
        },
        {
            label: 'No: Ignore invalid certificates',
            value: false
        }
    ];

    // ===============================================================
    // ===============================================================

    /**
     * Constructor
     */
    constructor() {
        super();

        this.configure('S3BucketApiModel', (model: CpmCS3BucketApiModel) => model.name);
        this.config.width = '60rem';
        this.contentMinHeight = '25rem';
        this.modelHumanName = 'S3 Bucket';

        this.updateModalTitle();
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling is done in the parent)
     */
    protected async onReady(): Promise<void> {
        this.form = new FormGroup({
            name: new FormControl<string | null>(null, [Validators.required]),
            description: new FormControl<string | null>(null, []),
            api_endpoint: new FormControl<string | null>(null, [Validators.required]),
            region: new FormControl<string | null>(null, [Validators.required]),
            bucket: new FormControl<string | null>(null, [Validators.required]),
            access_key: new FormControl<string | null>(null, [Validators.required]),
            access_secret: new FormControl<string | null>(null, [Validators.required]),
            verify_ssl: new FormControl<boolean | null>(null, [Validators.required])
        });

        if (this.dialogOpenMode !== EDialogOpenMode.NEW) {
            this.loadingStr = 'Loading the S3 Bucket...';
            this.item = await firstValueFrom(
                CpmCS3BucketApiService.getS3BucketByUUID(this.config.data?.extended?.existing?.uuid ?? '')
            );

            this.form.patchValue(this.item);

            if (!this.isCreationMode) {
                this.form.controls['api_endpoint'].disable();
                this.form.controls['region'].disable();
                this.form.controls['bucket'].disable();
                this.form.controls['verify_ssl'].disable();

                this.form.controls['access_key'].clearValidators();
                this.form.controls['access_key'].updateValueAndValidity();
                this.form.controls['access_secret'].clearValidators();
                this.form.controls['access_secret'].updateValueAndValidity();
            }

            this.form.updateValueAndValidity();
        }
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * On submit
     */
    protected async onSubmit() {
        if (!this.canSubmit()) {
            return;
        }

        const toSubmit: CpmCS3BucketCreateApiSchema | CpmCS3BucketUpdateApiSchema = this.isCreationMode
            ? {
                  name: this.form.controls['name']?.value,
                  description: this.form.controls['description']?.value,
                  api_endpoint: this.form.controls['api_endpoint']?.value,
                  region: this.form.controls['region']?.value,
                  bucket: this.form.controls['bucket']?.value,
                  access_key: this.form.controls['access_key']?.value,
                  access_secret: this.form.controls['access_secret']?.value,
                  verify_ssl: this.form.controls['verify_ssl']?.value
              }
            : {
                  name: this.form.controls['name']?.value,
                  description: this.form.controls['description']?.value,
                  access_key: this.form.controls['access_key']?.value,
                  access_secret: this.form.controls['access_secret']?.value
              };

        await this.submitCU(
            null,
            CpmCS3BucketApiService.createS3Bucket,
            CpmCS3BucketApiService.updateS3Bucket,
            this.item?.uuid,
            toSubmit,
            (resultCRUD: CpmCS3BucketApiModel) => new NewEditS3BucketModalResult(resultCRUD)
        );
    }
}
